/**
 * Pure helpers for synthesizing hidden lab-result fields on the form builder
 * whenever the "Expected Lab Results" list on a SampleId field changes.
 *
 * Kept free of React / store state so it's trivially unit-testable the moment
 * the portal gets a test framework.
 */

const LAB_RESULT_KEY_PREFIX = 'lab_result_';

const toSnake = (value) => {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const normalizeAnalyte = (analyte) => {
  if (!analyte || !analyte.name) return null;
  const name = String(analyte.name).trim();
  if (!name) return null;
  const unit = analyte.unit ? String(analyte.unit).trim() : '';
  const rawType = analyte.type ? String(analyte.type).trim().toLowerCase() : '';
  const type = rawType === 'text' ? 'Text' : 'Number';
  return { name, unit: unit || undefined, type };
};

const isLabResultFieldFor = (field, sampleIdFieldKey) =>
  !!field?.props?.labResultSource &&
  field.props.labResultSource.sampleIdFieldKey === sampleIdFieldKey;

const generateUniqueKey = (name, usedKeys) => {
  const snake = toSnake(name) || 'param';
  let candidate = `${LAB_RESULT_KEY_PREFIX}${snake}`;
  if (!usedKeys.has(candidate)) return candidate;
  let counter = 2;
  while (usedKeys.has(`${candidate}_${counter}`)) counter++;
  return `${candidate}_${counter}`;
};

const buildLabResultField = (sampleIdFieldKey, analyte, key) => {
  const isText = analyte.type === 'Text';
  return {
    id: key,
    type: isText ? 'input' : 'number',
    componentId: isText ? 'text' : 'number',
    props: {
      name: key,
      label: analyte.name,
      required: false,
      disabled: false,
      nameManuallySet: true,
      isHidden: true,
      labResultSource: {
        sampleIdFieldKey,
        analyteName: analyte.name,
        ...(analyte.unit ? { unitHint: analyte.unit } : {}),
      },
    },
  };
};

const fieldIsTextType = (field) => field?.componentId === 'text' || field?.type === 'input' || field?.type === 'text';
const fieldResolvedType = (field) => (fieldIsTextType(field) ? 'Text' : 'Number');

/**
 * Reconcile the fields list against a new expected-lab-results declaration on a SampleId field.
 *
 * Semantics:
 *   - Same analyte name (case-insensitive) as before → existing field kept; `label`, `analyteName`,
 *     `unitHint` refreshed from the new list. The field's `key` is preserved so submission history
 *     and filter/analytics references remain valid.
 *   - New analyte → synthesized as a hidden Number field with a stable `lab_result_{snake}` key,
 *     inserted directly after the SampleId field (grouped with its siblings).
 *   - Removed analyte → field is dropped from the schema. Historical `FormSubmissionField` rows
 *     in the DB remain untouched; re-adding the analyte later reuses the same stable key.
 *
 * @param {Object} sampleIdField the current SampleId field (must have `.id` and `.props.name`)
 * @param {Array} expectedAnalytes array of `{ name, unit? }`
 * @param {Array} existingFields full builder fields array
 * @returns {Array} new fields array (never mutates the input)
 */
export const synthesizeLabResultFields = (sampleIdField, expectedAnalytes, existingFields) => {
  if (!Array.isArray(existingFields)) return [];
  const sampleIdKey = sampleIdField?.props?.name;
  if (!sampleIdKey) return existingFields;

  const cleaned = (Array.isArray(expectedAnalytes) ? expectedAnalytes : [])
    .map(normalizeAnalyte)
    .filter(Boolean);

  const expectedByLower = new Map();
  for (const analyte of cleaned) {
    const lower = analyte.name.toLowerCase();
    if (!expectedByLower.has(lower)) expectedByLower.set(lower, analyte);
  }

  // Pass 1: split existing fields into (a) non-lab, kept in place; (b) lab fields that are still
  // expected, kept but updated; dropped otherwise. Lab fields are moved to the end in pass 2.
  const nonLabFields = [];
  const keptLabFields = [];

  for (const field of existingFields) {
    if (!isLabResultFieldFor(field, sampleIdKey)) {
      nonLabFields.push(field);
      continue;
    }
    const analyteName = field.props.labResultSource.analyteName;
    const lower = analyteName ? analyteName.toLowerCase() : null;
    const expected = lower ? expectedByLower.get(lower) : null;
    if (!expected) continue; // analyte removed — drop

    // If the expected type differs from the existing field type, rebuild the field shell so
    // type/componentId flip correctly (Number ↔ Text). Preserve the stable key.
    const currentType = fieldResolvedType(field);
    const key = field.props?.name || field.id;
    if (currentType !== expected.type) {
      keptLabFields.push(buildLabResultField(sampleIdKey, expected, key));
      continue;
    }

    keptLabFields.push({
      ...field,
      props: {
        ...field.props,
        label: expected.name,
        isHidden: true,
        labResultSource: {
          sampleIdFieldKey: sampleIdKey,
          analyteName: expected.name,
          ...(expected.unit ? { unitHint: expected.unit } : {}),
        },
      },
    });
  }

  // Pass 2: synthesize brand-new lab fields for analytes not yet represented.
  const presentLowerNames = new Set(
    keptLabFields.map((f) => f.props.labResultSource.analyteName.toLowerCase())
  );

  const usedKeys = new Set(
    [...nonLabFields, ...keptLabFields]
      .map((f) => f.props?.name || f.id)
      .filter(Boolean)
  );

  const additions = [];
  for (const analyte of cleaned) {
    if (presentLowerNames.has(analyte.name.toLowerCase())) continue;
    const key = generateUniqueKey(analyte.name, usedKeys);
    usedKeys.add(key);
    additions.push(buildLabResultField(sampleIdKey, analyte, key));
  }

  // All lab-result fields live at the end of the form, grouped together and below the user
  // fields. Order among lab fields follows the cleaned expected-analytes order.
  const labFieldByLowerName = new Map();
  for (const f of [...keptLabFields, ...additions]) {
    labFieldByLowerName.set(f.props.labResultSource.analyteName.toLowerCase(), f);
  }
  const orderedLabFields = cleaned
    .map((a) => labFieldByLowerName.get(a.name.toLowerCase()))
    .filter(Boolean);

  return [...nonLabFields, ...orderedLabFields];
};

export const __internals = { toSnake, generateUniqueKey, buildLabResultField };
